import moment from 'jalali-moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie,
  PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import AnalyticsLayout, { DATE_ALL, DateRange, toToman } from './AnalyticsLayout';

const STATUS_LABELS = globalEnum.orderStatus2;

// Brand palette only — blue / sky blue / gray / near-black (no rainbow).
const STATUS_COLORS: Record<string, string> = {
  Sent: '#1A6BFF',
  Created: '#94A3B8',
  Canceled: '#0F1923',
  WaitForCommerceBearingApproval: '#7A8CA0',
  WaitForCommerceFeltBowlApproval: '#64748B',
  WaitForWarehouseApproval: '#4D8FFF',
  InProgress: '#1254D6',
  WarehouseApproved: '#3D4F6B',
  UnapprovedCommerceBearing: '#334155',
  UnapprovedCommerceFeltBowl: '#475569',
  EditedOrder: '#93C5FD',
};

function statusClass(s: string) {
  if (s === 'Sent') return 'statusSent';
  if (s === 'Canceled') return 'statusCanceled';
  if (s.includes('Wait') || s.includes('Progress')) return 'statusWaiting';
  if (s === 'Created') return 'statusCreated';
  return 'statusOther';
}

export default function Orders() {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<DateRange>({ from: DATE_ALL, to: DATE_ALL });
  const [data, setData] = useState<any>(null);

  const fetchData = useCallback(async () => {
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.orders).get(apiQuery);
    if (res.code === 200) setData(res.data);
    dispatch(setLoading(false));
  }, [dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchData(); }, [fetchData]);

  const statusPieData = data?.ordersByStatus
    ? Object.entries(data.ordersByStatus).map(([k, v]) => ({
        name: STATUS_LABELS[k] || k,
        value: v as number,
        color: STATUS_COLORS[k] || '#94a3b8',
      }))
    : [];

  const exportTopOrders = () => {
    if (!data?.topOrders) return;
    const rows = data.topOrders.map((o: any, i: number) => ({
      '#': i + 1,
      'کد سفارش': o.code || o.id,
      'کاربر': o.userName,
      'موبایل': o.phoneNumber,
      'وضعیت': STATUS_LABELS[o.status] || o.status,
      'مبلغ نهایی (تومان)': toToman(o.finalPrice),
      'تاریخ ثبت': o.placedDate ? moment(o.placedDate).format('jYYYY/jMM/jDD') : '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'سفارشات برتر');
    XLSX.writeFile(wb, 'top-orders.xlsx');
  };

  return (
    <AnalyticsLayout tab="orders" title="تحلیل سفارشات" dateRange={dateRange} onDateChange={setDateRange}>

      {/* KPI row */}
      <div className="analyticsKpiRow">
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiGreen">💰</div>
          <div className="analyticsKpiValue">{tools.formatPrice(toToman(data?.totalIncome))} تومان</div>
          <div className="analyticsKpiLabel">مجموع فروش</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiBlue">📦</div>
          <div className="analyticsKpiValue">{data?.totalOrders ?? 0}</div>
          <div className="analyticsKpiLabel">کل سفارشات ثبت‌شده</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiOrange">✅</div>
          <div className="analyticsKpiValue">{data?.ordersByStatus?.Sent ?? 0}</div>
          <div className="analyticsKpiLabel">سفارشات ارسال‌شده</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiPurple">❌</div>
          <div className="analyticsKpiValue">{data?.ordersByStatus?.Canceled ?? 0}</div>
          <div className="analyticsKpiLabel">سفارشات لغو‌شده</div>
        </div>
      </div>

      {/* Charts row 1 */}
      <div className="analyticsChartGrid">
        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">تعداد سفارشات ماهانه</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.monthlyData || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: any) => [v, 'تعداد سفارش']} labelStyle={{ textAlign: 'right' }} />
              <Bar dataKey="count" fill="#1A6BFF" radius={[6, 6, 0, 0]} name="تعداد سفارشات" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">درآمد ماهانه (ارسال‌شده)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.monthlyData || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} width={90} />
              <Tooltip formatter={(v: any) => [tools.formatPrice(toToman(v)) + ' تومان', 'درآمد']} labelStyle={{ textAlign: 'right' }} />
              <Bar dataKey="income" fill="#3D4F6B" radius={[6, 6, 0, 0]} name="درآمد" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie chart */}
      {statusPieData.length > 0 && (
        <div className="analyticsChartCard" style={{ marginBottom: 20 }}>
          <div className="analyticsChartTitle">توزیع وضعیت سفارشات</div>
          <div style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ResponsiveContainer width={280} height={260}>
              <PieChart>
                <Pie data={statusPieData} dataKey="value" cx="50%" cy="50%" outerRadius={110} innerRadius={50} paddingAngle={2}>
                  {statusPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v: any, n: any) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, direction: 'rtl', minWidth: 200 }}>
              {statusPieData.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                  <span style={{ color: '#374151' }}>{s.name}</span>
                  <span style={{ marginRight: 'auto', fontWeight: 700, color: '#111' }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top orders table */}
      <div className="analyticsTableCard">
        <div className="analyticsTableHeader">
          <div className="analyticsChartTitle" style={{ margin: 0 }}>بزرگترین سفارشات (بر اساس مبلغ)</div>
          <button className="analyticsExportBtn" onClick={exportTopOrders}>⬇ خروجی اکسل</button>
        </div>
        <table className="alternating-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>#</th>
              <th>کد سفارش</th>
              <th>کاربر</th>
              <th>تاریخ</th>
              <th>وضعیت</th>
              <th>مبلغ (تومان)</th>
              <th>فاکتور</th>
            </tr>
          </thead>
          <tbody>
            {(data?.topOrders || []).map((o: any, i: number) => (
              <tr key={o.id} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                <td>{i + 1}</td>
                <td>{o.code || o.id}</td>
                <td>
                  <div>{o.userName || '—'}</div>
                  {o.phoneNumber && <div style={{ fontSize: 11, color: '#888', direction: 'ltr' }}>{o.phoneNumber}</div>}
                </td>
                <td>{o.placedDate ? moment(o.placedDate).format('jYYYY/jMM/jDD') : '—'}</td>
                <td><span className={`statusBadge ${statusClass(o.status)}`}>{STATUS_LABELS[o.status] || o.status}</span></td>
                <td><strong>{tools.formatPrice(toToman(o.finalPrice))}</strong></td>
                <td><Link to={`/order/invoice/${o.id}`} target="_blank">فاکتور</Link></td>
              </tr>
            ))}
            {(!data?.topOrders || data.topOrders.length === 0) && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#aaa' }}>داده‌ای یافت نشد</td></tr>
            )}
          </tbody>
        </table>
      </div>

    </AnalyticsLayout>
  );
}
